/**
 * Heartbeat — the Viktor pattern: observe how the operator actually works,
 * then propose ONE tailored, scoped automation per day.
 *
 * Pure detectors over real store snapshots, each returning a
 * `HeartbeatProposal` or null:
 *
 *   - pastDueChasing ......... ≥2 past-due unpaid invoices → offer to
 *     auto-draft payment reminders daily for anything 30+ days past due
 *   - followUpDiscipline ..... ≥5 overdue follow-ups → offer a daily 8am
 *     sweep drafting re-engagement emails for anything 14+ days quiet
 *   - weeklyUpdateCadence .... ≥2 customers with a sendable weekly update
 *     → offer to auto-compose updates every Monday for approval
 *
 * Every proposal carries REAL evidence lines (counts, names, day figures
 * drawn from the records that tripped the detector) — never a manufactured
 * pattern. `pickHeartbeatProposal` enforces the cadence contract: at most
 * ONE proposal per calendar day, skipping anything dismissed within the
 * last 14 days. Approving a proposal turns on exactly ONE scoped
 * automation (src/lib/agent/automations.ts) — never a global switch, and
 * the automation itself only QUEUES drafts into the approvals inbox.
 *
 * PURE by contract: no store, no network, no clock reads (callers pass
 * `now`), inputs untouched.
 */

import type { Account, BillingDate, Person } from '../db/types';
import { resolveWeeklyUpdateRecipient } from '../crm/weeklyUpdate';

const DAY_MS = 86_400_000;

/**
 * Every scoped automation id. The first three are Heartbeat-proposed
 * ("approve once"); 'content-autopilot' is DEFAULT-ENABLED because
 * drafting is safe automation — it never sends or publishes anything
 * (see src/lib/content/contentAutopilot.ts).
 */
export type AutomationId =
  | 'past-due-reminders'
  | 'followup-sweep'
  | 'weekly-updates'
  | 'content-autopilot';

/** One "approve once, runs on autopilot" proposal. */
export interface HeartbeatProposal {
  id: AutomationId;
  /** The observed pattern, e.g. "3 invoices are past due…". */
  headline: string;
  /** Real numbers/names from the records that tripped the detector. */
  evidence: string[];
  /** Plain-English scope of what turning it on does (drafts only). */
  whatItWillDo: string;
  /** Human cadence, e.g. "Every day · 8am". */
  cadenceLabel: string;
}

/** A bound detector — closes over the live snapshots it reads. */
export type HeartbeatDetector = () => HeartbeatProposal | null;

/** Dismissals stay suppressed this many days ("Not now", not "never"). */
export const DISMISS_SUPPRESSION_DAYS = 14;

/** Whole days a (YYYY-MM-DD or ISO) date is past `now`; ≤ 0 = not yet. */
function daysPast(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / DAY_MS);
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

// ---------------------------------------------------------------------------
// Detectors
// ---------------------------------------------------------------------------

/** Detector threshold: this many past-due invoices = a chasing pattern. */
export const PAST_DUE_INVOICE_THRESHOLD = 2;

/**
 * ≥2 past-due, unpaid invoices across live customers → the operator is
 * chasing payments by hand. Proposes the daily past-due reminder drafter.
 */
export function detectPastDueChasing(
  people: readonly Person[],
  now: number,
): HeartbeatProposal | null {
  const hits: { person: Person; invoice: BillingDate; days: number }[] = [];
  for (const p of people) {
    if (p.category !== 'customer' || p.status === 'churned') continue;
    for (const bd of p.billingDates ?? []) {
      if ((bd.status ?? 'scheduled') === 'paid') continue;
      const days = daysPast(bd.date, now);
      if (days >= 1) hits.push({ person: p, invoice: bd, days });
    }
  }
  if (hits.length < PAST_DUE_INVOICE_THRESHOLD) return null;

  hits.sort((a, b) => b.days - a.days);
  const customers = new Set(hits.map((h) => h.person.id)).size;
  const evidence = [
    `${plural(hits.length, 'invoice')} past due across ${plural(customers, 'customer')}`,
    ...hits.slice(0, 3).map(
      (h) =>
        `${h.person.name} · ${h.invoice.label} · ${plural(h.days, 'day')} past due` +
        (h.invoice.amount != null ? ` · $${h.invoice.amount.toLocaleString('en-US')}` : ''),
    ),
  ];
  return {
    id: 'past-due-reminders',
    headline: `${plural(hits.length, 'invoice')} go past due and you chase them by hand`,
    evidence,
    whatItWillDo:
      'Auto-draft a payment reminder every day for each invoice 30+ days past due. ' +
      'Every draft queues in the approvals inbox for your one-click approve — nothing sends itself.',
    cadenceLabel: 'Every day',
  };
}

/** Detector threshold: this many overdue follow-ups = slipping discipline. */
export const OVERDUE_FOLLOWUP_THRESHOLD = 5;

/**
 * ≥5 follow-ups fully past their date on live relationships → follow-up
 * discipline is slipping. Proposes the daily 8am re-engagement sweep.
 */
export function detectFollowUpDiscipline(
  people: readonly Person[],
  now: number,
): HeartbeatProposal | null {
  const overdue = people
    .filter((p) => {
      if (!p.followUpDate) return false;
      if (p.stage === 'closed-lost' || p.status === 'churned') return false;
      const t = Date.parse(p.followUpDate);
      // Grant the due day itself — overdue only once the day fully passed.
      return !Number.isNaN(t) && t + DAY_MS <= now;
    })
    .map((p) => ({ person: p, days: daysPast(p.followUpDate!, now) }))
    .sort((a, b) => b.days - a.days);
  if (overdue.length < OVERDUE_FOLLOWUP_THRESHOLD) return null;

  const evidence = [
    `${plural(overdue.length, 'follow-up')} overdue right now`,
    ...overdue.slice(0, 3).map(
      (o) => `${o.person.name} · follow-up ${plural(o.days, 'day')} overdue`,
    ),
  ];
  return {
    id: 'followup-sweep',
    headline: `${plural(overdue.length, 'follow-up')} have slipped past their date`,
    evidence,
    whatItWillDo:
      'Run a daily 8am sweep that drafts a re-engagement email for every relationship ' +
      '14+ days quiet. Each draft queues for your approval — never an auto-send.',
    cadenceLabel: 'Every day · 8am',
  };
}

/** Detector threshold: this many sendable weekly updates = a cadence. */
export const WEEKLY_UPDATE_THRESHOLD = 2;

/**
 * ≥2 accounts with a resolvable weekly-update recipient (the same canonical
 * resolver the send pipeline uses) → a weekly cadence worth automating.
 * Proposes the Monday weekly-update composer.
 */
export function detectWeeklyUpdateCadence(
  accounts: readonly Account[],
  people: readonly Person[],
): HeartbeatProposal | null {
  const due = accounts.filter(
    (a) => resolveWeeklyUpdateRecipient(a, [...people]) !== null,
  );
  if (due.length < WEEKLY_UPDATE_THRESHOLD) return null;

  const evidence = [
    `${plural(due.length, 'customer')} with a weekly update ready to send`,
    ...due.slice(0, 3).map((a) => `${a.name} · recipient on file`),
  ];
  return {
    id: 'weekly-updates',
    headline: `${plural(due.length, 'customer')} expect weekly updates you compose by hand`,
    evidence,
    whatItWillDo:
      'Auto-compose each weekly customer update every Monday, strictly from real ' +
      'workspace data, and queue every email for your approval before it goes out.',
    cadenceLabel: 'Every Monday',
  };
}

// ---------------------------------------------------------------------------
// The picker — at most ONE proposal per day, dismissed ones skipped
// ---------------------------------------------------------------------------

/** Same local calendar day? (The "one per day" unit is a day, not 24h.) */
export function sameLocalDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** Was `id` dismissed within the last 14 days? */
export function isDismissed(
  id: AutomationId,
  dismissed: Readonly<Record<string, string>>,
  now: number,
): boolean {
  const at = dismissed[id];
  if (!at) return false;
  const t = Date.parse(at);
  if (Number.isNaN(t)) return false;
  return now - t < DISMISS_SUPPRESSION_DAYS * DAY_MS;
}

/**
 * Pick today's single proposal: null when one was already proposed today
 * (whatever the user did with it), otherwise the first detector hit whose
 * id isn't inside its 14-day dismissal window. Detector order is the
 * priority order.
 */
export function pickHeartbeatProposal(
  detectors: readonly HeartbeatDetector[],
  dismissed: Readonly<Record<string, string>>,
  now: number,
  lastProposedAt: string | null,
): HeartbeatProposal | null {
  if (lastProposedAt) {
    const t = Date.parse(lastProposedAt);
    if (!Number.isNaN(t) && sameLocalDay(t, now)) return null;
  }
  for (const detect of detectors) {
    const proposal = detect();
    if (!proposal) continue;
    if (isDismissed(proposal.id, dismissed, now)) continue;
    return proposal;
  }
  return null;
}
